import React, { FC, useState } from 'react';
import {
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, Alert, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { healthWalletApi, useRemoveOrgMutation } from './healthWalletSlice';
// ⚠️ useAppDispatch, NOT react-redux's useDispatch: the plain one is typed Dispatch<UnknownAction> and
// rejects a thunk, so updateQueryData's patch comes back as `unknown` and patch.undo() won't compile.
// Every other screen that patches this cache uses the typed one.
import { useAppDispatch } from '../../app/hooks';
import type { EpicFhirOrg } from '../../types/onboarding';

/**
 * Delete a provider the PATIENT added via the "missing provider?" picker.
 *
 * ★ WHY IT EXISTS (Leo 2026-10-04): if we let a patient name a provider, we have to let them take one
 * back — the two-Moffitts case means a wrong pick is not a careless mistake but an easy one. Without
 * this, a mis-picked org sits in the list forever with a Connect button inviting them to authenticate
 * against a provider that is not theirs.
 *
 * THREE GUARDS, all enforced SERVER-SIDE (/ias/remove-org) — these are only the UI's mirror of them:
 *  1. `addedByUser` only. A Kno2-DISCOVERED org cannot be deleted: the next discovery brings it
 *     straight back, so offering it would be a lie. Skip is the answer for those.
 *  2. NOT CONNECTED (Leo 2026-10-04). An authenticated org holds a live Epic verification and token.
 *     Removing the row would hide the provider from every screen while the grant stayed active,
 *     leaving a credential the patient can no longer see or manage — the opposite of what "remove"
 *     promises. Skip handles a connected org they no longer want; Revoke handles the grant.
 *  3. Nothing retrieved. Records are stored per PATIENT, not per org, so once documents or resources
 *     have landed, deleting the org would strip the provenance of data that stays in the wallet.
 *
 * Renders NOTHING when any guard fails — no disabled button. A greyed trash icon would invite
 * "why can't I?" on every connected provider, when the honest answer is that delete simply does not
 * apply to them.
 *
 * See project_hw_epic_direct_connect.
 */

/** The UI mirror of the server's guards — see the three listed above. */
export const canRemoveProvider = (org: EpicFhirOrg): boolean =>
  org.addedByUser === true
  && org.status !== 'authed'
  && (org.documentsRetrieved ?? 0) === 0
  && (org.resourcesInserted ?? 0) === 0;

export const ProviderRemoveButton: FC<{
  org: EpicFhirOrg;
  /** Disabled while a sync is in flight — the org may be mid-retrieval. */
  disabled?: boolean;
}> = ({ org, disabled }) => {
  const dispatch = useAppDispatch();
  const [removeOrg] = useRemoveOrgMutation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canRemoveProvider(org)) return null;

  const name = org.displayName ?? 'this provider';

  const handleRemove = async () => {
    setError(null);
    setBusy(true);
    // Optimistic: drop the row so the list reacts at once. The server MARKS it rather than splicing
    // (indices are positional and ride the OAuth state), but the patient should see it go immediately.
    const patch = dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft: any) => {
        if (!draft?.epicFhirOrgs) return;
        draft.epicFhirOrgs = draft.epicFhirOrgs.filter(
          (o: EpicFhirOrg) => !(o.requestId === org.requestId && o.index === org.index)
        );
      })
    );
    try {
      await removeOrg({ iasRequestId: String(org.requestId), orgIndex: org.index }).unwrap();
      setConfirming(false);
    } catch (e: any) {
      // ⛔ Never a log-only catch — the row must come back and the reason must be on screen.
      // A 409 `records_retrieved` is the EXPECTED refusal (records arrived between render and click),
      // so show the server's sentence rather than a generic failure.
      patch.undo();
      setError(e?.data?.message || e?.data?.error || "We couldn't remove that provider. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="Remove this provider">
        <span>
          <IconButton
            size="small"
            aria-label={`Remove ${name}`}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
            sx={{ color: '#9aa0b5', '&:hover': { color: '#c0736b' } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Confirmed rather than instant: the row vanishes from every screen, and although the server
          keeps the entry (so re-picking restores it) that is invisible to the patient. */}
      <Dialog open={confirming} onClose={() => { if (!busy) { setConfirming(false); setError(null); } }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove {name}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            You added this provider yourself. You haven't signed in to it and we haven't brought in any
            records from it, so nothing will be lost. You can add it again at any time.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setConfirming(false); setError(null); }} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleRemove()}
            disabled={busy}
            variant="contained"
            color="error"
            disableElevation
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
